/**
 * The words and the colours this page prints an enum by.
 *
 * They are here rather than in the section that draws them because the **export**
 * prints the same words (`tables.ts`), and a CSV that called a `retainer` line
 * "Retainer" in one table and "retainer" in another would be two vocabularies for
 * one column.
 *
 * The palette assignments are CONTRACT.md §2.13's rule applied once: `--c1..--c4` are
 * categorical, "in order and never cycled", and `--s1..--s7` are sequential. Four
 * billing models are categories; three market tiers are one ordered scale.
 */

import type { PackageShape, RecognisedModel } from '@fareast/shared'

import type { ChartSlot } from '../../../components/portal/Chart.js'
import type { Formatter } from '../../../format/useFormat.js'
import type { PipelineData } from './data.js'
import { WHOLE_AMOUNT } from './render.js'

/** The word a billing model is printed by. The enum is lower-case on the wire. */
export const MODEL_LABEL: Record<RecognisedModel, string> = {
  retainer: 'Retainer',
  project: 'Project',
  extra: 'Extra',
  usage: 'Usage',
}

/** Categorical, in the order lines are recognised in. */
export const MODEL_SLOT: Record<RecognisedModel, ChartSlot> = {
  retainer: 'c1',
  project: 'c2',
  extra: 'c3',
  usage: 'c4',
}

/** Sequential, darkest for Tier 1. */
export const TIER_SLOT: Record<number, ChartSlot> = { 1: 's6', 2: 's4', 3: 's2' }

/** The fallback for a tier the palette has no entry for — a fourth would be new. */
export const TIER_SLOT_FALLBACK: ChartSlot = 's4'

/** `custom` — built for one client; `preset` — a named, reusable package. */
export const SHAPE_LABEL: Record<PackageShape, string> = {
  custom: 'Custom',
  preset: 'Preset',
}

export type PipelineItem = PipelineData['items'][number]

/**
 * A project's one-time total, or a package change's **signed monthly** delta.
 *
 * `AnalyticsPipelineSchema`: "a one-time total and a package change's $/mo delta are
 * different units and are never summed into one figure." The unit is on the figure
 * for the same reason.
 */
export function pipelineAmountLabel(item: PipelineItem, format: Formatter): string {
  if (item.amount === null) return 'Not priced'
  if (item.kind === 'project') return format.money(item.amount, WHOLE_AMOUNT)
  const sign = item.amount.amount > 0 ? '+' : ''
  return `${sign}${format.money(item.amount, WHOLE_AMOUNT)}/mo`
}
